// Pure geometry + (de)serialization helpers for freehand drawing strokes.
// A stroke is persisted as a CanvasShape of kind "drawing"; its point path and
// style live in the shape's `content` column via serializeDrawing/parseDrawing.
// Points are stored RELATIVE to the shape's bounding-box origin (see addDrawing),
// so rendering and moving never need to touch the point list.

export type Point = [number, number];

export interface DrawingContent {
  points: Point[];       // relative to the shape bbox origin
  color: string;         // NODE_COLOR_PALETTE name
  width: number;         // stroke width in flow units
  highlighter?: boolean; // semi-transparent thick stroke
}

// Round to 2dp and drop trailing zeros so path strings stay compact + stable.
function f(v: number): string {
  return String(Number(v.toFixed(2)));
}

export function bbox(points: Point[]): { x: number; y: number; width: number; height: number } {
  if (points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

function mid(a: Point, b: Point): Point {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

// Build an SVG path `d` string. With smoothing (default) uses quadratic curves
// through segment midpoints for a natural freehand look; without, a raw polyline.
export function strokePath(points: Point[], smooth = true): string {
  if (points.length === 0) return "";
  const first = points[0];
  if (points.length === 1) return `M ${f(first[0])} ${f(first[1])} L ${f(first[0])} ${f(first[1])}`;

  if (!smooth) {
    return "M " + points.map(([x, y]) => `${f(x)} ${f(y)}`).join(" L ");
  }

  let d = `M ${f(first[0])} ${f(first[1])}`;
  for (let i = 1; i < points.length - 1; i++) {
    const ctrl = points[i];
    const end = mid(points[i], points[i + 1]);
    d += ` Q ${f(ctrl[0])} ${f(ctrl[1])} ${f(end[0])} ${f(end[1])}`;
  }
  const last = points[points.length - 1];
  d += ` L ${f(last[0])} ${f(last[1])}`;
  return d;
}

// Shortest distance from point p to line segment a→b (clamped to endpoints).
export function distToSegment(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  let t = lenSq === 0 ? 0 : ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const cx = a[0] + t * dx;
  const cy = a[1] + t * dy;
  return Math.hypot(p[0] - cx, p[1] - cy);
}

// True if p is within `tolerance` of any segment of the stroke.
export function hitTestStroke(points: Point[], p: Point, tolerance: number): boolean {
  if (points.length === 0) return false;
  if (points.length === 1) return Math.hypot(p[0] - points[0][0], p[1] - points[0][1]) <= tolerance;
  for (let i = 0; i < points.length - 1; i++) {
    if (distToSegment(p, points[i], points[i + 1]) <= tolerance) return true;
  }
  return false;
}

// Collapse a stroke to a straight line between its first and last point.
export function straighten(points: Point[]): Point[] {
  if (points.length < 2) return points;
  return [points[0], points[points.length - 1]];
}

// Perpendicular distance from point p to the infinite line through a→b.
// Falls back to point distance when a and b coincide.
function perpDist(p: Point, a: Point, b: Point): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  const area2 = Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]);
  return area2 / Math.sqrt(lenSq);
}

// Ramer–Douglas–Peucker: drop points that lie within `epsilon` of the segment
// between the retained endpoints, preserving corners and overall shape. Used on
// stroke commit to keep stored point lists (and re-render cost) small.
export function simplify(points: Point[], epsilon: number): Point[] {
  if (points.length <= 2) return points.slice();
  const first = points[0];
  const last = points[points.length - 1];
  let maxDist = 0;
  let idx = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const d = perpDist(points[i], first, last);
    if (d > maxDist) { maxDist = d; idx = i; }
  }
  if (maxDist > epsilon) {
    const left = simplify(points.slice(0, idx + 1), epsilon);
    const right = simplify(points.slice(idx), epsilon);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}

export function serializeDrawing(c: DrawingContent): string {
  return JSON.stringify(c);
}

export function parseDrawing(content: string | undefined): DrawingContent | null {
  if (!content) return null;
  try {
    const obj = JSON.parse(content) as Partial<DrawingContent>;
    if (!Array.isArray(obj.points)) return null;
    return {
      points: obj.points as Point[],
      color: obj.color ?? "indigo",
      width: obj.width ?? 4,
      highlighter: obj.highlighter,
    };
  } catch {
    return null;
  }
}
